import React from 'react';

import { format } from 'date-fns';

import { useOptionalActualVsBudgetReportContext } from './context/ActualVsBudgetReportContext';
import { resolveVisibleColumns } from './helpers/reportVisibleColumns';
import { columnsForPreset, DEFAULT_COLUMN_PRESET, DETAILED_SUPPLEMENTARY_COLUMNS, PRESET_BOARD, PRESET_FULL, PRESET_STANDARD } from './types/ReportColumns';
import { LEVEL_DEPARTMENT, LEVEL_DEPARTMENT_COST_CENTRE, LEVEL_FULL_DETAILS } from './types/ReportType';

import Alert from 'CommonComponents/Alert';

const LEVEL_LABELS = {
    [LEVEL_DEPARTMENT]: $t('Department'),
    [LEVEL_DEPARTMENT_COST_CENTRE]: $t('Department + Cost Centre'),
    [LEVEL_FULL_DETAILS]: $t('Full details'),
};

const COLUMN_PRESET_LABELS = {
    [PRESET_BOARD]: $t('Board summary'),
    [PRESET_STANDARD]: $t('Standard'),
    [PRESET_FULL]: $t('Full'),
};

const Chip = ({ label, value }) => (
    <span className="tw-rounded-full tw-border tw-border-grey tw-bg-background tw-px-3 tw-py-1 tw-text-2xs tw-font-normal tw-text-charcoal-75">
        {label} <b className="tw-font-semibold tw-text-charcoal">{value}</b>
    </span>
);

const MetaItem = ({ label, value }) => (
    <span>
        {label} <b className="tw-font-semibold tw-text-charcoal">{value}</b>
    </span>
);

// Formats the value's own year-month rather than round-tripping through
// `new Date(string)`: a date-only string parses as UTC midnight while format()
// renders local time, so browsers west of UTC would show the previous month.
const monthName = (date) => {
    if (!date) {
        return null;
    }
    const [year, month] = String(date).split(/[-T]/);
    return format(new Date(Number(year), Number(month) - 1, 1), 'MMM yyyy');
};

/**
 * The report's own header: what was asked for (title + configuration chips),
 * what it covers (entity / period / month / generated) and any warning about the
 * data itself. Rendered above every report level so a report read on screen — or
 * screenshotted into a board pack — carries its own provenance.
 *
 * Reads everything from the report context on the live Report tab, and falls back
 * to `storedReport` when there is no context — an archived report re-opened from
 * the Generated Reports list renders through props instead, showing every chip its
 * stored parameters can vouch for and dropping the ones they cannot.
 */
/**
 * @param {Object}  props
 * @param {number}  props.unmatchedCount     Comparison lines with no match in the current structure.
 * @param {string}  props.comparisonName     Short name of the comparison version, for the banner.
 * @param {boolean} props.showComparisonChip Whether this level can compare versions at all. The
 *                                           department / cost-centre drill-downs render no comparison
 *                                           columns, so a "Compare to" chip there would promise
 *                                           figures the table never shows.
 * @param {Object}  props.storedReport       A generated report's own parameters, for a report opened
 *                                           outside the configuration form. Keys mirror
 *                                           ActualCostsReportResource: name, budget_name,
 *                                           comparison_budget_name, start_date, end_date,
 *                                           report_level, generated_at, plus entityName.
 */
const ActualVsBudgetReportHeader = ({ unmatchedCount = 0, comparisonName = '', showComparisonChip = true, storedReport = null }) => {
    const context = useOptionalActualVsBudgetReportContext();
    const { configs, criteria, level, columnPreset, visibleColumns, hideEmptyRows, generationTime, viewType } = context;

    // Nothing to describe: no live configuration form AND no stored report.
    if (!criteria && !storedReport) {
        return null;
    }

    const budgets = configs?.budgets || [];
    const reportName = storedReport ? storedReport.name : criteria.name;
    const reportLevel = storedReport ? storedReport.report_level : level;
    const startDate = storedReport ? storedReport.start_date : criteria?.start_date;
    const endDate = storedReport ? storedReport.end_date : criteria?.end_date;
    const entityName = storedReport ? storedReport.entityName : configs?.entityName;

    // Live, the version names come from the form's budget list; for a stored
    // report the server resolved them (including versions since deleted, which a
    // client-side list of live budgets could not name).
    const budgetName = storedReport ? storedReport.budget_name : budgets.find(({ id }) => String(id) === String(criteria.budget_id))?.name;
    const comparisonBudgetName = storedReport
        ? storedReport.comparison_budget_name
        : budgets.find(({ id }) => String(id) === String(criteria.comparison_budget_id))?.name;

    // The column set is stored with a generated report, so an archived report can
    // name the columns it is actually rendering. "Hide rows with no values" is
    // still live-only client state, so that chip drops outside the report form
    // rather than showing a default it cannot vouch for.
    const activePreset = storedReport ? storedReport.column_preset || DEFAULT_COLUMN_PRESET : columnPreset;
    const activeColumns = storedReport ? resolveVisibleColumns({}, storedReport) : visibleColumns;
    const hasDisplayState = activePreset !== undefined && activePreset !== null;
    const hasHideEmptyRowsState = !storedReport && hasDisplayState;

    // "Standard (customised)" whenever the visible set no longer matches the
    // preset it started from — the chip has to describe the columns on screen,
    // not the button that was last pressed.
    const presetDefaults = hasDisplayState ? columnsForPreset(activePreset) : {};
    const isCustomised = hasDisplayState && DETAILED_SUPPLEMENTARY_COLUMNS.some((key) => !!presetDefaults[key] !== !!(activeColumns || {})[key]);
    const presetLabel = COLUMN_PRESET_LABELS[activePreset] || activePreset;

    const generatedAt = storedReport ? storedReport.generated_at : generationTime && viewType ? generationTime[viewType] : null;

    return (
        <div className="tw-space-y-3">
            {/* The report's own name. Omitted while configuring a report on the
                Report tab (it has no name yet) so the page heading isn't echoed. */}
            {!!reportName && <h2 className="tw-text-lg tw-font-semibold tw-text-blue">{reportName}</h2>}

            <div className="tw-flex tw-flex-wrap tw-gap-2">
                {!!LEVEL_LABELS[reportLevel] && <Chip label={$t('Report level')} value={LEVEL_LABELS[reportLevel]} />}
                {!!budgetName && <Chip label={$t('Version')} value={budgetName} />}
                {showComparisonChip && <Chip label={$t('Compare to')} value={comparisonBudgetName || $t('No comparison')} />}
                {hasDisplayState && <Chip label={$t('Columns')} value={isCustomised ? $t('{{preset}} (customised)', { preset: presetLabel }) : presetLabel} />}
                {hasHideEmptyRowsState && <Chip label={$t('Hide empty rows')} value={hideEmptyRows ? $t('On') : $t('Off')} />}
            </div>

            <div className="tw-flex tw-flex-wrap tw-gap-x-[22px] tw-gap-y-1.5 tw-rounded-[10px] tw-border tw-border-grey tw-px-4 tw-py-2.5 tw-text-xs tw-text-charcoal-75">
                {!!entityName && <MetaItem label={$t('Entity')} value={entityName} />}
                {!!startDate && !!endDate && <MetaItem label={$t('Period')} value={`${monthName(startDate)} – ${monthName(endDate)}`} />}
                {!!endDate && <MetaItem label={$t('Month')} value={monthName(endDate)} />}
                <MetaItem label={$t('Generated')} value={generatedAt ? format(new Date(generatedAt), 'd MMM yyyy, h:mm a') : $t('Not yet generated')} />
            </div>

            {unmatchedCount > 0 && (
                <Alert type="warning">
                    {/* Two whole sentences rather than one with an interpolated
                        count: the verb has to agree with it ("1 line has" vs
                        "3 lines have"), and a single string can only get that
                        right for one of the two. */}
                    {unmatchedCount === 1
                        ? $t(
                              'Structures differ between versions: 1 line in {{version}} has no match in the current structure and is shown as "Not in current structure".',
                              { version: comparisonName || comparisonBudgetName || $t('the comparison version') },
                          )
                        : $t(
                              'Structures differ between versions: {{count}} lines in {{version}} have no match in the current structure and are shown as "Not in current structure".',
                              {
                                  count: unmatchedCount,
                                  version: comparisonName || comparisonBudgetName || $t('the comparison version'),
                              },
                          )}
                </Alert>
            )}
        </div>
    );
};

export default ActualVsBudgetReportHeader;
